import React, { useEffect, useState } from 'react';

import {
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';

import { reportBudget } from 'loot-core/client/queries';
import { useSpreadsheet } from 'loot-core/client/SpreadsheetProvider';
import { type BudgetTemplateLine } from 'loot-core/shared/budget-template';
import { monthsOfCalendarYear, monthsOfFiscalYear, sheetForMonth } from 'loot-core/shared/months';
import { amountToCurrency } from 'loot-core/shared/util';
import {
  type CategoryEntity,
  type CategoryGroupEntity,
} from 'loot-core/types/models';

import { theme } from '../../../style';
import { SlideToggle } from '../../common/SlideToggle';
import { View } from '../../common/View';
import { type ITableTotals } from '../types';
import { getMonthlyBudget, getYearlyBudget } from '../util';

import { getSpendingDistributionData } from './chart-data';
import {
  AggregateMode,
  chartTooltipStyle,
  Datasource,
  DimensionMode,
  toggleStyle,
} from './shared';
import { useGlobalPref } from '../../../hooks/useGlobalPref';

function CustomTooltip({ payload }: any) {
  if (!payload || !payload[0]) {
    return <></>;
  }

  const data = payload[0].payload;

  return (
    <View style={chartTooltipStyle}>
      Name: {data.payload.name}
      <br />
      Amount: {amountToCurrency(data.payload.value)}
      <br />
      Percentage: {(data.payload.percentage * 100).toFixed(2)}%
    </View>
  );
}

type ISpendingDistributionChartProps = {
  year: number;
  budgetLines: BudgetTemplateLine[];
  categories: CategoryEntity[];
  categoryGroups: CategoryGroupEntity[];
  groupTotals: { [groupId: string]: ITableTotals };
  datasource: Datasource;
};

export function SpendingDistributionChart({
  year,
  budgetLines,
  categoryGroups,
  categories,
  groupTotals,
  datasource,
}: ISpendingDistributionChartProps) {
  const [fiscalYearStart] = useGlobalPref('fiscalYearStart');
  const [dimensionMode, setDimensionMode] = useState(
    DimensionMode.CategoryGroup,
  );
  const [aggregateMode, setAggregateMode] = useState(
    AggregateMode.MonthlyAverage,
  );
  const [chartData, setChartData] = useState(null);
  const spreadsheet = useSpreadsheet();

  useEffect(() => {
    getSpendingDistributionData(
      categoryGroups,
      categories,
      dimensionMode,
      aggregateMode,
      async function (catGroup) {
        if (datasource === Datasource.BudgetTemplate) {
          return groupTotals[catGroup.id];
        } else if (datasource === Datasource.AnnualGrid) {
          let total = 0;
          for (const month of monthsOfFiscalYear(year, fiscalYearStart)) {
            total += (
              await spreadsheet.get(
                sheetForMonth(month.month),
                reportBudget.groupBudgeted(catGroup.id),
              )
            ).value;
          }
          return {
            monthly: Math.floor(total / 12),
            yearly: total,
          };
        } else {
          throw new Error('Unknown data source');
        }
      },
      async cat => {
        if (datasource === Datasource.BudgetTemplate) {
          const bl = budgetLines.find(bl => bl.category === cat.id);
          return (
            bl && {
              monthly: getMonthlyBudget(bl),
              yearly: getYearlyBudget(bl),
            }
          );
        } else if (datasource === Datasource.AnnualGrid) {
          let total = 0;
          for (const month of monthsOfFiscalYear(year, fiscalYearStart)) {
            total += (
              await spreadsheet.get(
                sheetForMonth(month.month),
                reportBudget.catBudgeted(cat.id),
              )
            ).value;
          }
          return {
            monthly: Math.floor(total / 12),
            yearly: total,
          };
        } else {
          throw new Error('Unknown data source');
        }
      },
    ).then(data => {
      setChartData(data);
    });
  }, [dimensionMode, aggregateMode, categoryGroups, year]);
  const hasOther = chartData?.findIndex(ch => ch.name === 'Other') !== -1;

  return (
    <View data-adoption-id="spending-distribution-projection">
      <h3>Spending Distribution</h3>

      <View style={toggleStyle} onClick={(e) => e.preventDefault()}>
        Category Groups
        <SlideToggle
          onToggle={val =>
            setDimensionMode(
              val ? DimensionMode.Category : DimensionMode.CategoryGroup,
            )
          }
        />
        Category
      </View>
      <View style={{ ...toggleStyle, paddingTop: 10 }} onClick={(e) => e.preventDefault()}>
        Monthly Average
        <SlideToggle
          onToggle={val =>
            setAggregateMode(
              val ? AggregateMode.YearlyTotal : AggregateMode.MonthlyAverage,
            )
          }
        />
        Yearly Total
      </View>
      {hasOther && dimensionMode === DimensionMode.Category ? (
        <View style={{ margin: '5px 0', color: theme.warningText }}>
          Displaying the top 10 by amount
        </View>
      ) : null}

      {chartData && (
        <ResponsiveContainer height={300} style={{ marginTop: 10 }}>
          <PieChart>
            <Legend layout="horizontal" verticalAlign="bottom" align="center" />
            <Tooltip content={<CustomTooltip />} />
            <Pie
              data={chartData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      )}
    </View>
  );
}
